import {useCallback, useEffect, useMemo, useState} from 'react';
import AccessConfigContext from 'src/internal/config/AccessConfigContext';
import DebugContext from 'src/internal/contexts/DebugContext';
import XContext from 'src/internal/contexts/XContext';
import {baseExperiments, baseFlags} from 'src/internal/defaults';
import RegistriesProvider from 'src/internal/providers/RegistriesProvider/RegistriesProvider';

import type {ReactElement, ReactNode} from 'react';
import type {
  AccessConfig,
  Experiment,
  Flag,
  InnerExperiments,
  InnerFlags,
} from 'src/types';

type Props = {
  children: ReactNode;
  config: AccessConfig;
};

interface FlagData {
  providerName: string;
  flagName: Flag;
  enabled: boolean;
}

interface SailAccessFlagForceEvent {
  type: 'SAIL_ACCESS_FLAG_FORCE';
  data: FlagData;
}

interface SailAccessFlagUpdatedEvent {
  type: 'SAIL_ACCESS_FLAG_UPDATED';
  data: FlagData;
}

declare global {
  interface Window {
    SAIL_CORE_DEV_TOOLS_PRESENT: boolean;
  }
}

type SailEvent = SailAccessFlagForceEvent | SailAccessFlagUpdatedEvent;

/**
 * @see https://sail.stripe.me/apis/access/AccessProvider/
 */
export default function AccessProvider({
  children,
  config,
}: Props): ReactElement {
  const {experiments, flags, name = 'default'} = config;

  // ----- Experiments section:

  const [overriddenExperiments, setOverriddenExperiments] = useState({});

  const forceExperiment = useCallback(
    <TName extends Experiment>(name: TName, value: InnerExperiments[TName]) => {
      if (baseExperiments.hasOwnProperty(name)) {
        return;
      }

      setOverriddenExperiments((overriddenExperiments) => {
        return {...overriddenExperiments, [name]: value};
      });
    },
    [],
  );

  // ----- Flags section:

  const [overriddenFlags, setOverriddenFlags] = useState({});

  const forceFlag = useCallback(
    <TName extends Flag>(name: TName, value: InnerFlags[TName]) => {
      if (baseFlags.hasOwnProperty(name)) {
        return;
      }

      setOverriddenFlags((overriddenFlags) => {
        return {...overriddenFlags, [name]: value};
      });
    },
    [],
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.SAIL_CORE_DEV_TOOLS_PRESENT) {
      return;
    }

    const handleMessage = (event: MessageEvent<SailEvent>) => {
      if (name !== event.data?.data?.providerName) {
        return;
      }

      if (event.data?.type === 'SAIL_ACCESS_FLAG_FORCE') {
        forceFlag(event.data.data.flagName, event.data.data.enabled);

        window.postMessage({
          type: 'SAIL_ACCESS_FLAG_UPDATED',
          data: {
            providerName: name,
            flagName: event.data.data.flagName,
            enabled: event.data.data.enabled,
          },
        });
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [forceFlag, name]);

  // ----- Context objects:

  const allExperiments = useMemo(
    () => ({
      ...experiments!,
      ...overriddenExperiments,
      ...baseExperiments,
    }),
    [experiments, overriddenExperiments],
  );

  const allFlags = useMemo(
    () => ({
      ...flags!,
      ...overriddenFlags,
      ...baseFlags,
    }),
    [flags, overriddenFlags],
  );

  const x = useMemo(
    () => ({
      experiments: allExperiments,
      flags: allFlags,
    }),
    [allExperiments, allFlags],
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.SAIL_CORE_DEV_TOOLS_PRESENT) {
      return;
    }

    window.postMessage({
      type: 'SAIL_ACCESS_CONFIG',
      data: {
        ...x,
        providerName: name,
      },
    });
    // eslint-disable-next-line @stripe-internal/stripe/exhaustive-deps
  }, []);

  const debug = useMemo(
    () => ({
      forceExperiment,
      forceFlag,
    }),
    [forceExperiment, forceFlag],
  );

  return (
    <AccessConfigContext.Provider value={config}>
      <RegistriesProvider>
        <DebugContext.Provider value={debug}>
          <XContext.Provider value={x}>{children}</XContext.Provider>
        </DebugContext.Provider>
      </RegistriesProvider>
    </AccessConfigContext.Provider>
  );
}
